import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Truck, Building2, ArrowLeft, Loader2, Sparkles, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [betaStatus, setBetaStatus] = useState(null);
  const [codeValid, setCodeValid] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    phone: '',
    role: 'enterprise',
    beta_code: ''
  });

  useEffect(() => {
    // Fetch beta status
    fetch(`${API_URL}/api/beta/status`)
      .then(res => res.json())
      .then(data => setBetaStatus(data))
      .catch(() => setBetaStatus({ beta_enabled: false }));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // Reset code validation when code changes
    if (e.target.name === 'beta_code') {
      setCodeValid(null);
    }
  };

  const validateBetaCode = async () => {
    if (!formData.beta_code) return;
    
    try {
      const res = await fetch(`${API_URL}/api/beta/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: formData.beta_code })
      });
      const data = await res.json();
      setCodeValid(data.valid);
      if (!data.valid) {
        toast.error(data.message);
      }
    } catch {
      setCodeValid(false);
    }
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      toast.error(t('auth.termsRequired'));
      return;
    }

    if (betaStatus?.require_code && !formData.beta_code) {
      toast.error('Code beta requis');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({ ...formData, terms_accepted: termsAccepted });
      toast.success(t('auth.registerButton') + ' ✓');
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <LanguageSwitcher />
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
              <Truck className="w-7 h-7 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold font-['Chivo'] tracking-tight">{t('auth.register')}</CardTitle>
            <CardDescription>Rejoignez l&apos;écosystème {t('common.appName')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Role Selection */}
              <div className="space-y-3">
                <Label>{t('auth.accountType')}</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleRoleChange('enterprise')}
                    className={`flex flex-col items-center justify-between rounded-lg border-2 p-4 transition-all ${
                      formData.role === 'enterprise'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid="role-enterprise"
                  >
                    <Building2 className="mb-2 h-6 w-6 text-slate-600" />
                    <span className="text-sm font-medium">{t('auth.roleEnterprise')}</span>
                    <span className="text-xs text-slate-500">{t('auth.roleEnterpriseDesc')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleChange('carrier')}
                    className={`flex flex-col items-center justify-between rounded-lg border-2 p-4 transition-all ${
                      formData.role === 'carrier'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid="role-carrier"
                  >
                    <Truck className="mb-2 h-6 w-6 text-slate-600" />
                    <span className="text-sm font-medium">{t('auth.roleCarrier')}</span>
                    <span className="text-xs text-slate-500">{t('auth.roleCarrierDesc')}</span>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">{t('auth.companyName')}</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Votre société"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  data-testid="register-company-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+33 6 00 00 00 00"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>

              {/* Beta Code */}
              {betaStatus?.beta_enabled && betaStatus?.require_code && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="beta_code" className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      Code Beta
                    </Label>
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                      {betaStatus.spots_remaining} places restantes
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="beta_code"
                      name="beta_code"
                      type="text"
                      placeholder="FLITFLOW-BETA-XXX"
                      value={formData.beta_code}
                      onChange={handleChange}
                      onBlur={validateBetaCode}
                      className={`uppercase ${
                        codeValid === true ? 'border-green-500 focus:ring-green-500' : 
                        codeValid === false ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                      required
                      data-testid="register-beta-code-input"
                    />
                    {codeValid === true && <CheckCircle2 className="w-5 h-5 text-green-500 self-center" />}
                    {codeValid === false && <XCircle className="w-5 h-5 text-red-500 self-center" />}
                  </div>
                  <p className="text-xs text-slate-500">
                    Entrez votre code d&apos;invitation pour accéder à la beta privée
                  </p>
                </div>
              )}

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="terms" 
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
                  {t('auth.termsAccept')}{' '}
                  <a href="/compliance" target="_blank" className="text-orange-500 hover:underline">
                    {t('auth.termsLink')}
                  </a>
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={loading || !termsAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.registerButton')
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
